// 1. React and core dependencies
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';

// 2. UI Components
import { Button, Card, Col, Form, Row, Dropdown, InputGroup } from 'react-bootstrap';

// 3. Libraries
// eslint-disable-next-line import/no-extraneous-dependencies
import Stepper from 'react-stepper-horizontal';
import CsLineIcons from 'cs-line-icons/CsLineIcons';

import 'react-toastify/dist/ReactToastify.css';
import * as Yup from 'yup';
import ClipLoader from 'react-spinners/ClipLoader';
import { setCurrentUser, setIsLoginUser } from 'slices/auth/authSlice';

// 4. Utils
import { handlerErrors } from 'utils/handleErrors';

// 5. Components
import HtmlHead from 'components/html-head/HtmlHead';
import InputText from 'components/form-elements/InputText';

// 6. API calls
import { getVerificationStatus, onboardingProfile, onboardingComplete, getVerificationUpdate } from 'api/Api-user';
import { ToastSuccess } from '../../components/alerts/toast';

function VerifyAccount() {
  const title = 'Verificar Cuenta';
  const description = 'Verificar Cuenta Plataforma Autopaquete';
  const history = useHistory();
  const dispatch = useDispatch();
  const metamapRef = useRef(null);
  const status = {
    PROFILE_INFO: 0,
    IDENTITY_VERIFICATION: 1,
    BILLING_INFO: 2,
  };
  const [currentStep, setCurrentStep] = useState(() => {
    const onboardingStep = localStorage.getItem('onboardingStep');
    return onboardingStep ? status[onboardingStep] : '';
  });
  // const [currentStep, setCurrentStep] = useState(2);
  const { currentUser } = useSelector((state) => state.auth);
  // console.log('current user:', currentUser);
  // console.log('current step:', currentStep);

  const steps = [{ title: 'Perfil' }, { title: 'Verificación' }, { title: 'Facturación' }];
  const [isLoading, setIsLoading] = useState(false);
  const initialProfileStates = {
    fullName: '',
    businessName: '',
    webSide: '',
    businessDescription: '',
  };
  const [profileData, setProfileData] = useState(initialProfileStates);
  const [errors, setErrors] = useState(initialProfileStates);
  const [billingData, setBillingData] = useState({
    legalName: '',
    rfc: '',
    system: '',
    street: '',
    streetNumber: '',
    zip: '',
    neighborhood: '',
    city: '',
    state: '',
    email: '',
    wantsInvoice: null, // sin seleccionar por defecto,
  });
  const noBillingData = {
    email: currentUser.email,
    wantsInvoice: false,
  };
  const [billingErrors, setBillingErrors] = useState({
    legalName: '',
    rfc: '',
    system: '',
    street: '',
    streetNumber: '',
    zip: '',
    neighborhood: '',
    city: '',
    state: '',
    email: '',
    wantsInvoice: '',
  });
  const fiscalRegimes = [
    { code: '601', name: 'General de Ley Personas Morales' },
    { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
    { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
    { code: '606', name: 'Arrendamiento' },
    { code: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
    { code: '608', name: 'Demás Ingresos' },
    { code: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
    { code: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
    { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
    { code: '614', name: 'Ingresos por intereses' },
    { code: '615', name: 'Régimen de los ingresos por obtención de premios' },
    { code: '616', name: 'Sin obligaciones fiscales' },
    { code: '620', name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
    { code: '621', name: 'Incorporación Fiscal' },
    { code: '622', name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
    { code: '623', name: 'Opcional para Grupos de Sociedades' },
    { code: '624', name: 'Coordinados' },
    { code: '625', name: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
    { code: '626', name: 'Régimen Simplificado de Confianza' },
  ];
  // Schema Validation
  const validationSchemaProfile = Yup.object().shape({
    fullName: Yup.string().required('Por favor, ingresa tu nombre completo'),
    businessName: Yup.string().required('Por favor, ingresa el nombre de tu empresa'),
    webSide: Yup.string().required('Ingresa el sitio web de tu empresa.'),
    businessDescription: Yup.string().required('Por favor, describe brevemente los productos o servicios que ofrece tu empresa'),
  });
  const billingValidationSchema = Yup.object().shape({
    wantsInvoice: Yup.boolean().nullable().required('Selecciona si deseas factura'),
    legalName: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa la razón social o nombre completo para facturar'),
    }),
    rfc: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa el RFC'),
    }),
    system: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Selecciona el régimen fiscal'),
    }),
    street: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa el nombre de la calle'),
    }),
    streetNumber: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa el número exterior'),
    }),
    zip: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa el código postal'),
    }),
    neighborhood: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa la colonia o barrio'),
    }),
    city: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa la ciudad'),
    }),
    state: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Selecciona el estado'),
    }),
    email: Yup.string().when('wantsInvoice', {
      is: true,
      then: (schema) => schema.required('Ingresa el correo electrónico de facturación').email('Ingresa un correo electrónico válido'),
    }),
  });
  const updateProfileField = (value, validation, field) => {
    // Remove spaces and special characters solo si el campo NO es 'webpage'
    // const cleanedData = field === 'webpage' ? value : cleanData(value);
    if (value === '' || validation.test(value)) {
      setProfileData((prevValues) => ({ ...prevValues, [field]: value }));
    }
  };
  // - cleanError
  const cleanError = (value, field) => {
    if (errors[field]) {
      // console.log('si hay un error en el campo:', field);
      setErrors((prevValues) => ({ ...prevValues, [field]: value }));
    }
  };
  const handlerOnboardingProfile = async () => {
    try {
      setIsLoading(true);
      const response = await onboardingProfile(profileData);
      // console.log('response handlerOnboardingProfile:', response);
      if (response.success) {
        setCurrentStep(1);
      }
    } catch (error) {
      handlerErrors(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handlerOnboardingComplete = async () => {
    try {
      setIsLoading(true);
      const dataToSend = billingData?.wantsInvoice ? billingData : noBillingData;
      const response = await onboardingComplete(dataToSend);
      // console.log('response handlerOnboardingComplete:', response);
      if (response.success) {
        history.push('/inicio');
        ToastSuccess(response.message);
      }
    } catch (error) {
      handlerErrors(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handlerVerificationStatus = async () => {
    try {
      const response = await getVerificationStatus();
      // console.log('response handlerVerificationStatus:', response);
      if (response.success !== undefined) {
        const step = status[response.onboarding_step];
        if (step !== undefined) {
          setCurrentStep(step);
        }
      }
    } catch (error) {
      handlerErrors(error);
    }
  };
  // Validation
  const validateProfileData = async (e) => {
    e.preventDefault();
    try {
      await validationSchemaProfile.validate(profileData, {
        // context: { isInvoice: isChecked },
        abortEarly: false, // Esto asegura que se capturen todos los errores en lugar de detenerse en el primero.
      });
      handlerOnboardingProfile();
    } catch (error) {
      const formErrors = {};
      error.inner.forEach((err) => {
        formErrors[err.path] = err.message;
      });
      setErrors(formErrors);
    }
  };
  const validateBillingData = async (e) => {
    e.preventDefault();
    try {
      await billingValidationSchema.validate(billingData, {
        abortEarly: false, // Esto asegura que se capturen todos los errores en lugar de detenerse en el primero.
      });
      // console.log('utimo paso:', billingData);
      handlerOnboardingComplete();
    } catch (error) {
      const formErrors = {};
      error.inner.forEach((err) => {
        formErrors[err.path] = err.message;
      });
      setBillingErrors(formErrors);
    }
  };
  const handleWantsInvoiceChange = (value) => {
    setBillingData((prev) => {
      if (value === false) {
        return {
          ...prev,
          wantsInvoice: false,
          // email: currentUser.email,
        };
      }
      return {
        ...prev,
        wantsInvoice: true,
      };
    });
  };
  // Get status verification
  useEffect(() => {
    handlerVerificationStatus();
  }, []);
  // on meta map eventListeners
  useEffect(() => {
    const el = metamapRef.current;
    // console.log('current ref:', el);
    if (!el) return;
    // const handleLoaded = () => console.log('✅ Metamap loaded');
    const handleStarted = () => {
      // console.log('🚀 Usuario inició verificación');
      getVerificationUpdate('userStartedSdk');
    };
    const handleFinished = (event) => {
      // console.log('🎉 Verificación finalizada', event.detail);
      // Avanza automáticamente al paso de facturación
      // setCurrentStep(2);

      getVerificationUpdate('userFinishedSdk');
    };
    const handleExited = () => {
      // console.log('❌ Usuario salió del SDK');
      getVerificationUpdate('exitedSdk');
    };
    // const handleContinued = () => console.log('📲 Continuó en otro dispositivo');
    const handleScreen = (event) => {
      // console.log('🖥 Cambio de pantalla', event.detail);
      if (event.detail.screen === 'verificationSuccess') {
        // console.log('pantalla verificationSuccess ', event.detail);
        // Avanza automáticamente al paso de facturación
        setCurrentStep(2);
        getVerificationUpdate('verificationSuccess');
      }
      if (event.detail.screen === 'verificationExpired') {
        // console.log('pantalla  verificationExpired', event.detail);
        getVerificationUpdate(' verificationExpired');
      }
    };

    el.addEventListener('metamap:userStartedSdk', handleStarted);
    el.addEventListener('metamap:userFinishedSdk', handleFinished);
    el.addEventListener('metamap:exitedSdk', handleExited);
    el.addEventListener('metamap:screen', handleScreen);
    // el.addEventListener('metamap:loaded', handleLoaded);
    // el.addEventListener('metamap:continuedOnAnotherDevice', handleContinued);
    // Cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      // el.removeEventListener('metamap:loaded', handleLoaded);
      el.removeEventListener('metamap:userStartedSdk', handleStarted);
      el.removeEventListener('metamap:userFinishedSdk', handleFinished);
      el.removeEventListener('metamap:exitedSdk', handleExited);
      // el.removeEventListener('metamap:continuedOnAnotherDevice', handleContinued);
      el.removeEventListener('metamap:screen', handleScreen);
    };
  }, [currentStep]);

  return (
    <>
      {/* <ModalSuccess message={messageSuccess} /> */}
      <div id="pageContainer">
        <HtmlHead title={title} description={description} />
        {/* Title Start */}
        <div className="page-title-container">
          <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back" to="/inicio">
            <CsLineIcons icon="chevron-left" size="13" />
            <span className="align-middle text-small ms-1">Inicio</span>
          </NavLink>
          <h1 className="mb-0 pb-0 display-4" id="title">
            {title}
          </h1>
        </div>
        {/* Title End */}
        <div className="mt-4">
          <Stepper
            steps={steps}
            activeStep={currentStep}
            activeColor="#850000" // Color del paso actual
            completeColor="#b30000" // Un tono un poco más claro para los pasos completados
            defaultColor="#d9c7c7" // Un gris claro rosado para los pasos pendientes
            defaultBarColor="#e5caca" // Color suave para la línea entre pasos
            className="stepper"
          />
          <Row>
            <Col lg="10" className="m-auto mt-4">
              <Card>
                <Card.Body>
                  {currentStep === 0 && (
                    <Row className="px-5">
                      <div className="text-center mb-3">
                        <span className="badge bg-primary">Paso 1</span>
                      </div>
                      <h4 className="text-center mb-2">Información de perfil</h4>
                      <p className="text-center mb-5">Esta información nos ayudará a personalizar la plataforma para ti</p>
                      <Col lg="12" className="position-relative mb-3">
                        <InputText
                          label="Nombre completo"
                          icon="user"
                          isRequired
                          setField={updateProfileField}
                          fieldName="fullName"
                          value={profileData.fullName}
                          cleanError={cleanError}
                          validation={/^[^<>]*$/u}
                        />
                        <div className={`invalid-tooltip ${errors.fullName ? 'd-block' : 'd-none'}`}>{errors.fullName}</div>
                      </Col>
                      <Col lg="12" className="position-relative mb-3">
                        <InputText
                          label="Nombre del negocio o empresa"
                          icon="shop"
                          value={profileData.businessName}
                          isRequired
                          fieldName="businessName"
                          setField={updateProfileField}
                          cleanError={cleanError}
                          validation={/^[^<>]+$/}
                        />
                        <div className={`invalid-tooltip ${errors.businessName ? 'd-block' : 'd-none'}`}>{errors.businessName}</div>
                      </Col>
                      <Col lg="12" className="position-relative mb-3">
                        <InputText
                          label="¿A qué se dedica tu empresa?"
                          icon="shop"
                          value={profileData.businessDescription}
                          isRequired
                          fieldName="businessDescription"
                          setField={updateProfileField}
                          cleanError={cleanError}
                          validation={/^[^<>]+$/}
                        />
                        <div className={`invalid-tooltip ${errors.businessDescription ? 'd-block' : 'd-none'}`}>{errors.businessDescription}</div>
                      </Col>
                      <Col lg="12" className="position-relative mb-3">
                        <InputText
                          label="Sitio web de tu empresa"
                          icon="at-sign"
                          isRequired
                          value={profileData.webSide}
                          // placeholder="Ejemplo: tusitio.com, instagram.com/tuperfil o facebook.com/tuperfil"
                          fieldName="webSide"
                          setField={updateProfileField}
                          cleanError={cleanError}
                          validation={/^[^<>]+$/}
                        />
                        <div className={`invalid-tooltip ${errors.webSide ? 'd-block' : 'd-none'}`}>{errors.webSide}</div>
                      </Col>
                    </Row>
                  )}
                  {currentStep === 1 && (
                    <Row>
                      <div className="text-center mb-3">
                        <span className="badge bg-primary">Paso 2</span>
                      </div>
                      <h4 className="text-center my-4">Verifica tu identidad</h4>
                      <Col className="mb-3">
                        <p className="w-60 m-auto mb-5 text-center">Se abrirá una ventana segura para subir tu identificación oficial.</p>
                        <div className="text-center">
                          <metamap-button
                            ref={metamapRef}
                            clientid="687aff16f418bb3129be13c7"
                            flowid="687aff1692186f1291397555"
                            metadata={JSON.stringify({ email_id: currentUser.email, fixedLanguage: 'es' })}
                            color="#850000"
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                  {currentStep === 2 && (
                    <Row className="px-5">
                      <div className="text-center mb-3">
                        <span className="badge bg-primary">Paso 3</span>
                      </div>
                      <h4 className="text-center my-4">Confirma tu preferencia de facturación</h4>
                      <p className="w-100 m-auto mb-5 text-center">
                        Esto nos permitirá emitir correctamente los comprobantes por cada depósito. Te asignaremos cuenta de depósito única, y cada vez que
                        deposites en ella, tu saldo se abonará automáticamente.
                      </p>
                      <Col lg="12" className="position-relative mb-3">
                        <Form.Label className="text-semi-large">¿Necesitas factura?</Form.Label>
                        <div style={{ position: 'relative', zIndex: 1, maxWidth: '200px' }}>
                          {/* <option value="">Selecciona una opción</option> */}
                          <div className="form-check ms-2">
                            <input
                              type="radio"
                              name="autoInvoice"
                              value="true"
                              className="form-check-input me-2"
                              checked={billingData?.wantsInvoice === true}
                              onChange={(e) => {
                                // setBillingData({ ...billingData, wantsInvoice: true });
                                handleWantsInvoiceChange(e.target.value === 'true');
                                setBillingErrors((prev) => ({ ...prev, wantsInvoice: false }));
                              }}
                            />
                            <label className="form-check-label">Sí</label>
                          </div>
                          <div className="form-check ms-2">
                            <input
                              type="radio"
                              name="autoInvoice"
                              value="false"
                              className="form-check-input me-2"
                              checked={billingData?.wantsInvoice === false}
                              onChange={(e) => {
                                handleWantsInvoiceChange(e.target.value === 'true');
                                // setBillingData({ ...billingData, wantsInvoice: false });
                                setBillingErrors((prev) => ({ ...prev, wantsInvoice: false }));
                              }}
                            />
                            <label className="form-check-label">No</label>
                          </div>
                          {billingErrors?.wantsInvoice && (
                            <div className={`invalid-tooltip ${billingErrors?.wantsInvoice ? 'd-block' : 'd-none'}`}>{billingErrors?.wantsInvoice}</div>
                          )}
                        </div>
                      </Col>
                      {billingData?.wantsInvoice === false && (
                        <p>
                          En caso de no facturar en este momento, se emitirán comprobantes simples por cada depósito.
                          <br />
                          Podrás habilitar la facturación posteriormente desde la plataforma.
                        </p>
                      )}
                      {billingData.wantsInvoice && (
                        <Form className="mb-5">
                          <p>
                            Por favor, ingresa tus datos fiscales exactamente como aparecen en tu constancia de situación fiscal, ya que serán verificados ante
                            el SAT
                          </p>
                          <Row className="g-3">
                            {/* <div>
                              Tu información será validada con el SAT. Asegúrate de usar los datos tal como aparecen en tu Constancia de Situación Fiscal.
                            </div> */}
                            <Col lg="8" className="position-relative">
                              <Form.Label>Razón Social</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="user" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  required
                                  type="text"
                                  value={billingData?.legalName}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, legalName: e.target.value.toUpperCase() });
                                    setBillingErrors((prev) => ({ ...prev, legalName: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.legalName && (
                                <div className={`invalid-tooltip ${billingErrors?.legalName ? 'd-block' : 'd-none'}`}>{billingErrors?.legalName}</div>
                              )}
                            </Col>
                            <Col lg="4" className="position-relative">
                              <Form.Label>RFC</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="shield" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  required
                                  type="text"
                                  value={billingData.rfc}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, rfc: e.target.value });
                                    setBillingErrors((prev) => ({ ...prev, rfc: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.rfc && <div className={`invalid-tooltip ${billingErrors?.rfc ? 'd-block' : 'd-none'}`}>{billingErrors?.rfc}</div>}
                            </Col>
                            <Col lg="8" className="position-relative">
                              <Form.Label>Correo</Form.Label> <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="email" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  required
                                  type="email"
                                  value={billingData.email}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, email: e.target.value });
                                    setBillingErrors((prev) => ({ ...prev, email: false }));
                                  }}
                                />
                              </InputGroup>
                              <span className="text-medium">Las facturas se enviarán a este correo</span>
                              {billingErrors?.email && (
                                <div className={`invalid-tooltip ${billingErrors?.email ? 'd-block' : 'd-none'}`}>{billingErrors?.email}</div>
                              )}
                            </Col>
                            <Col lg="4" className="position-relative">
                              <Form.Label>Régimen Fiscal</Form.Label>
                              <span className="text-primary">*</span>
                              <Dropdown className="w-100">
                                <Dropdown.Toggle
                                  variant="foreground-alternate"
                                  className="text-end shadow d-flex justify-content-between align-items-center w-100"
                                >
                                  {billingData?.system || 'Seleccionar'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow " style={{ maxHeight: '280px', overflowY: 'scroll' }}>
                                  {fiscalRegimes.map((element, index) => (
                                    <Dropdown.Item
                                      key={index}
                                      style={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                      }}
                                      className={`${billingData?.system === element.code && 'text-danger'}`}
                                      onClick={() => {
                                        setBillingData((prevValues) => {
                                          return { ...prevValues, system: element.code };
                                        });
                                        setBillingErrors((prev) => ({ ...prev, system: false }));
                                      }}
                                    >
                                      {`${element.code} | ${element.name}`}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>
                              {billingErrors?.system && (
                                <div className={`invalid-tooltip ${billingErrors?.system ? 'd-block' : 'd-none'}`}>{billingErrors?.system}</div>
                              )}
                            </Col>
                            <Col md="8" lg="8" className="position-relative">
                              <Form.Label>Calle</Form.Label> <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="destination" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  required
                                  value={billingData.street}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, street: e.target.value.toUpperCase() });
                                    setBillingErrors((prev) => ({ ...prev, street: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.street && (
                                <div className={`invalid-tooltip ${billingErrors?.street ? 'd-block' : 'd-none'}`}>{billingErrors?.street}</div>
                              )}
                            </Col>
                            <Col md="4" lg="4" className="position-relative">
                              <Form.Label>Número</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="sign" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={billingData?.streetNumber}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, streetNumber: e.target.value });
                                    setBillingErrors((prev) => ({ ...prev, streetNumber: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.streetNumber && (
                                <div className={`invalid-tooltip ${billingErrors?.streetNumber ? 'd-block' : 'd-none'}`}>{billingErrors?.streetNumber}</div>
                              )}
                            </Col>
                            <Col md="8" lg="8" className="position-relative">
                              <Form.Label>Colonia</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="pin" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={billingData.neighborhood}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, neighborhood: e.target.value.toUpperCase() });
                                    setBillingErrors((prev) => ({ ...prev, neighborhood: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.neighborhood && (
                                <div className={`invalid-tooltip ${billingErrors?.neighborhood ? 'd-block' : 'd-none'}`}>{billingErrors?.neighborhood}</div>
                              )}
                            </Col>
                            <Col md="4" lg="4" className="position-relative">
                              <Form.Label>C.P.</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="pin" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={billingData?.zip}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, zip: e.target.value });
                                    setBillingErrors((prev) => ({ ...prev, zip: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.zip && <div className={`invalid-tooltip ${billingErrors?.zip ? 'd-block' : 'd-none'}`}>{billingErrors?.zip}</div>}
                            </Col>
                            <Col md="8" lg="6" className="position-relative">
                              <Form.Label>Ciudad</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="building" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={billingData.city}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, city: e.target.value.toUpperCase() });
                                    setBillingErrors((prev) => ({ ...prev, city: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.city && (
                                <div className={`invalid-tooltip ${billingErrors?.city ? 'd-block' : 'd-none'}`}>{billingErrors?.city}</div>
                              )}
                            </Col>
                            <Col md="8" lg="6" className="position-relative">
                              <Form.Label>Estado</Form.Label>
                              <span className="text-primary">*</span>
                              <InputGroup>
                                <InputGroup.Text>
                                  <CsLineIcons icon="image" size="16" className="text-alternate" />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={billingData.state}
                                  onChange={(e) => {
                                    setBillingData({ ...billingData, state: e.target.value.toUpperCase() });
                                    setBillingErrors((prev) => ({ ...prev, state: false }));
                                  }}
                                />
                              </InputGroup>
                              {billingErrors?.state && (
                                <div className={`invalid-tooltip ${billingErrors?.state ? 'd-block' : 'd-none'}`}>{billingErrors?.state}</div>
                              )}
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Row>
                  )}
                </Card.Body>
              </Card>
              <div className="mt-4 d-flex justify-content-end">
                <Button
                  className="btn btn-primary me-2"
                  onClick={() =>
                    window.open(`https://wa.me/+523319774057/?text=${`Hola, tengo un problema con mi proceso de registro. ¿Podrían ayudarme, por favor?`}`)
                  }
                >
                  <span>Solicitar ayuda</span>
                </Button>
                {currentStep === 0 && (
                  <Button size="lg" disabled={isLoading} onClick={validateProfileData}>
                    {isLoading && <ClipLoader color="#FFFFFF" loading={isLoading} size={10} />}
                    {isLoading ? 'Siguiente' : ' Siguiente'}
                  </Button>
                )}
                {currentStep === 2 && (
                  <Button size="lg" disabled={isLoading} onClick={validateBillingData}>
                    {isLoading && <ClipLoader color="#FFFFFF" loading={isLoading} size={10} />}
                    {isLoading ? 'Enviando' : ' Enviar'}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default VerifyAccount;
